/**
 * Cliente HTTP de la base de datos del oficial (SPEC-BD §5.1): un wrapper por ruta de `/base`.
 *
 * Usa el mismo `req()` de campana-nueva/api.ts que `oficialApi`, por los mismos motivos: trae el token
 * de Activa (el tenant ES el token), el reintento del 401 y la renovación del token de operador, y
 * devuelve un 422 sin lanzar. Nunca se manda `brandId`, ni en el cuerpo ni en la query.
 *
 * Los cuerpos se arman campo por campo y no con el objeto que llega: así un campo de más que viaje
 * pegado a un estado de React (por ejemplo, un `brandId` en un objeto reusado) no llega al backend.
 */

import { req } from "../campana-nueva/api";
import { qs, type Resp } from "./api";
import type * as T from "./types.base";

const B = "/activa/oficial/base";

/** Un id o una clave de la URL van siempre codificados: un valor raro no tiene que poder cambiar la ruta. */
const e = (id: string) => encodeURIComponent(id);

export const baseApi = {
  // Resumen y opciones
  resumen: (): Promise<Resp<T.ResumenBase>> => req("GET", `${B}/resumen`),
  opciones: (): Promise<Resp<T.OpcionesBase>> => req("GET", `${B}/opciones`),

  // Cargas
  cargas: (): Promise<Resp<T.CargaPublica[]>> => req("GET", `${B}/cargas`),
  /** 201. El tope de 8 M caracteres se chequea antes de llamar (`logic/base.ts`): un 413 tarda en llegar. */
  crearCarga: (c: T.EntradaCarga): Promise<Resp<T.RespuestaCrearCarga>> =>
    req("POST", `${B}/cargas`, {
      ...(c.nombre !== undefined ? { nombre: c.nombre } : {}),
      nombreArchivo: c.nombreArchivo,
      archivo: { content: c.archivo.content, encoding: c.archivo.encoding },
    }),
  carga: (id: string): Promise<Resp<T.CargaPublica>> => req("GET", `${B}/cargas/${e(id)}`),
  /** Para retomar un borrador: ejemplos, ambigüedades y catálogo, con la misma forma que al subir. 404 = venció. */
  columnasCarga: (id: string): Promise<Resp<T.ColumnasCarga>> => req("GET", `${B}/cargas/${e(id)}/columnas`),
  previsualizar: (id: string, p: T.EntradaPrevisualizar): Promise<Resp<T.Previsualizacion>> =>
    req("POST", `${B}/cargas/${e(id)}/previsualizar`, {
      vinculo: p.vinculo, declaracion: p.declaracion, etiquetas: p.etiquetas, celularesArgentinos: p.celularesArgentinos,
    }),
  /** 202. `huella` es la de la última previsualización: si algo cambió, 409 CARGA_DESACTUALIZADA. */
  importar: (id: string, huella: string): Promise<Resp<T.CargaPublica>> => req("POST", `${B}/cargas/${e(id)}/importar`, { huella }),
  erroresCarga: (id: string, p: { page?: number; limit?: number } = {}): Promise<Resp<T.PaginaErroresCarga>> =>
    req("GET", `${B}/cargas/${e(id)}/errores${qs({ page: p.page, limit: p.limit })}`),
  /** Sólo un borrador o una fallida. Una importada se borra con `eliminarDatosCarga`. */
  descartarCarga: (id: string): Promise<Resp<{ descartada: true }>> => req("DELETE", `${B}/cargas/${e(id)}`),
  /** El público de una carga importada, para una campaña: el que ya había o uno nuevo. 409 CARGA_NO_IMPORTADA si no terminó. */
  publicoDeCarga: (id: string): Promise<Resp<T.PublicoDetalle>> => req("POST", `${B}/cargas/${e(id)}/publico`),
  /** 202. Borra de verdad los contactos que sólo vinieron de esta carga (SPEC-BD B-13). */
  eliminarDatosCarga: (id: string, motivo: string): Promise<Resp<T.CargaPublica>> =>
    req("POST", `${B}/cargas/${e(id)}/eliminar-datos`, { motivo }),

  // Contactos
  contactos: (f: T.FiltrosContactos = {}): Promise<Resp<T.PaginaContactos>> =>
    req("GET", `${B}/contactos${qs({
      q: f.q, etiqueta: f.etiqueta, carga: f.carga, consentimiento: f.consentimiento,
      noContactar: f.noContactar, sucursal: f.sucursal, page: f.page, limit: f.limit,
    })}`),
  /** Con `reveal` el backend devuelve el número completo y deja registrado quién lo vio. Sin él, ni se manda. */
  contacto: (id: string, reveal = false): Promise<Resp<T.ContactoFicha>> =>
    req("GET", `${B}/contactos/${e(id)}${qs({ reveal: reveal ? "true" : undefined })}`),
  cambiarEtiquetas: (id: string, c: T.CambioEtiquetas): Promise<Resp<T.ContactoFicha>> =>
    req("PATCH", `${B}/contactos/${e(id)}/etiquetas`, {
      ...(c.agregar ? { agregar: c.agregar } : {}),
      ...(c.quitar ? { quitar: c.quitar } : {}),
    }),
  cambiarConsentimiento: (id: string, c: T.CambioConsentimiento): Promise<Resp<T.ContactoFicha>> =>
    req("PUT", `${B}/contactos/${e(id)}/consentimiento`, c.optIn
      ? { optIn: true, fuente: c.fuente, fecha: c.fecha, constancia: true, motivo: c.motivo }
      : { optIn: false, motivo: c.motivo }),
  suprimirContacto: (id: string, s: T.EntradaSupresion): Promise<Resp<T.ResultadoSupresion>> =>
    req("POST", `${B}/contactos/${e(id)}/suprimir`, { motivo: s.motivo, dejarEnNoContactar: s.dejarEnNoContactar }),

  // Campos personalizados
  editarCampo: (clave: string, c: { etiqueta?: string; tipo?: T.TipoCampo }): Promise<Resp<T.CampoCatalogo>> =>
    req("PATCH", `${B}/campos/${e(clave)}`, {
      ...(c.etiqueta !== undefined ? { etiqueta: c.etiqueta } : {}),
      ...(c.tipo !== undefined ? { tipo: c.tipo } : {}),
    }),

  // Públicos
  publicos: (): Promise<Resp<T.PublicoResumen[]>> => req("GET", `${B}/publicos`),
  /** El conteo en vivo del editor: el mismo cálculo que el chequeo de una campaña, sin plantilla ni variables. */
  contarPublico: (filtro: T.FiltroPublico): Promise<Resp<T.ConteoPublico>> => req("POST", `${B}/publicos/contar`, { filtro }),
  /** 201. */
  crearPublico: (p: T.EntradaPublico): Promise<Resp<T.PublicoDetalle>> =>
    req("POST", `${B}/publicos`, { nombre: p.nombre, tipo: p.tipo, filtro: p.filtro }),
  /** Recalcula el conteo en el momento (`conteoActual`). */
  publico: (id: string): Promise<Resp<T.PublicoDetalle>> => req("GET", `${B}/publicos/${e(id)}`),
  editarPublico: (id: string, c: { nombre?: string; filtro?: T.FiltroPublico }): Promise<Resp<T.PublicoDetalle>> =>
    req("PATCH", `${B}/publicos/${e(id)}`, {
      ...(c.nombre !== undefined ? { nombre: c.nombre } : {}),
      ...(c.filtro !== undefined ? { filtro: c.filtro } : {}),
    }),
  /** 201. Sin nombre: «{nombre} (copia)». Sin tipo: el del original. Sin nada manda `{}` (algunos parsers dan 400 con cuerpo vacío). */
  duplicarPublico: (id: string, c: { nombre?: string; tipo?: T.TipoPublico } = {}): Promise<Resp<T.PublicoDetalle>> =>
    req("POST", `${B}/publicos/${e(id)}/duplicar`, {
      ...(c.nombre !== undefined ? { nombre: c.nombre } : {}),
      ...(c.tipo !== undefined ? { tipo: c.tipo } : {}),
    }),
  /** 409 PUBLICO_EN_USO (con `campanas` en el cuerpo) si lo usa una campaña en borrador. No borra contactos. */
  borrarPublico: (id: string): Promise<Resp<{ borrado: true }>> => req("DELETE", `${B}/publicos/${e(id)}`),
  contactosPublico: (id: string, p: { page?: number; limit?: number } = {}): Promise<Resp<T.PaginaContactos>> =>
    req("GET", `${B}/publicos/${e(id)}/contactos${qs({ page: p.page, limit: p.limit })}`),
};
